import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api-client.js';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { FormBanner } from '../components/ui/form.js';

type EventStatus = 'pending' | 'awaiting' | 'confirmed' | 'failed' | 'pending_approval';
type ChannelStatus = 'na' | 'pending' | 'sent' | 'completed' | 'failed' | 'verified' | 'caller_disconnect';
type Channel = 'voice' | 'sms' | 'email';

interface EventReminder { channels: Channel[]; offset: string }

interface RecurrenceRule {
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'weekdays';
  interval: number;
  weekdays?: string[];
  count?: number | null;
  until?: string | null;
}

interface Event {
  id: string;
  title: string;
  contact_profile_id?: string | null;
  message_template_id?: string | null;
  attendee_name?: string;
  attendee_email?: string;
  attendee_phone: string;
  start_at: string;
  end_at: string;
  recurrence?: RecurrenceRule | null;
  reminders: EventReminder[];
  status: EventStatus;
  delivery_status: Partial<Record<'voice' | 'sms' | 'email' | 'identity_verify', ChannelStatus>>;
  parent_event_id?: string | null;
  dnc: boolean;
  created_at: string;
  updated_at: string;
}

const STATUS_TONE: Record<EventStatus, 'neutral' | 'success' | 'warning' | 'danger'> = {
  pending_approval: 'warning',
  pending: 'neutral',
  awaiting: 'warning',
  confirmed: 'success',
  failed: 'danger',
};

const DELIVERY_TONE: Record<ChannelStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  na: 'neutral',
  pending: 'neutral',
  sent: 'info',
  completed: 'success',
  verified: 'success',
  failed: 'danger',
  caller_disconnect: 'warning',
};

export function EventDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const eventQ = useQuery({
    queryKey: ['event', id],
    queryFn: () => api.get(`/v1/events/${id}`) as Promise<Event>,
    enabled: !!id,
  });

  const cancel = useMutation({
    mutationFn: () => api.delete(`/v1/events/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['events'] });
      navigate('/events');
    },
  });

  const approve = useMutation({
    mutationFn: () => api.post(`/v1/admin/events/${id}/approve-booking`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event', id] });
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    },
  });

  // Phase 4b — operator-only timeline. 401 (no admin scope) just hides the
  // section; the page still works for plain read-only viewers.
  const timelineQ = useQuery({
    queryKey: ['admin', 'event-timeline', id],
    queryFn: () => api.get(`/v1/admin/events/${id}/timeline`) as Promise<EventTimeline>,
    enabled: !!id,
    retry: false,
    refetchInterval: 30_000,
  });

  if (eventQ.isLoading) return <p className="text-sm text-ink-muted">Loading…</p>;
  if (eventQ.error) return <FormBanner message={(eventQ.error as Error).message} />;
  if (!eventQ.data) return <FormBanner message="Event not found" />;
  const e = eventQ.data;

  return (
    <div className="max-w-3xl space-y-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <Link to="/events" className="text-sm text-ink-muted hover:text-ink">← Events</Link>
          <h1 className="text-2xl font-semibold mt-1">{e.title}</h1>
          <div className="flex items-center gap-2 mt-1 text-sm">
            <Badge tone={STATUS_TONE[e.status]}>{e.status}</Badge>
            {e.parent_event_id && (
              <Link to={`/events/${e.parent_event_id}`} className="text-ink-muted hover:text-ink">
                child of recurring series
              </Link>
            )}
            {e.dnc && <Badge tone="warning">DNC suppressed</Badge>}
          </div>
        </div>
        <div className="flex gap-2">
          {e.status === 'pending_approval' && (
            <Button
              variant="primary"
              onClick={() => approve.mutate()}
              disabled={approve.isPending}
            >
              {approve.isPending ? 'Approving…' : 'Approve booking'}
            </Button>
          )}
          <Link to={`/events/${e.id}/edit`}>
            <Button variant="secondary">Edit</Button>
          </Link>
          <Button
            variant="ghost"
            onClick={() => {
              if (window.confirm(`Cancel event "${e.title}"?`)) cancel.mutate();
            }}
            disabled={cancel.isPending}
          >
            Cancel event
          </Button>
        </div>
      </header>

      <Card>
        <CardHeader><CardTitle>When</CardTitle></CardHeader>
        <CardBody>
          <DefList
            items={[
              ['Start', new Date(e.start_at).toLocaleString()],
              ['End', new Date(e.end_at).toLocaleString()],
              ['Recurrence', e.recurrence ? <RecurrenceSummary rule={e.recurrence} /> : '— None —'],
            ]}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader><CardTitle>Attendee</CardTitle></CardHeader>
        <CardBody>
          <DefList
            items={[
              ['Name', e.attendee_name ?? '—'],
              [
                'Phone',
                <a className="text-accent hover:underline" href={`tel:${e.attendee_phone}`}>
                  <code>{e.attendee_phone}</code>
                </a>,
              ],
              [
                'Email',
                e.attendee_email ? (
                  <a className="text-accent hover:underline" href={`mailto:${e.attendee_email}`}>
                    {e.attendee_email}
                  </a>
                ) : '—',
              ],
            ]}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader><CardTitle>Reminders</CardTitle></CardHeader>
        <CardBody className="p-0">
          {e.reminders.length === 0 ? (
            <p className="px-4 py-3 text-sm text-ink-muted">No reminders configured.</p>
          ) : (
            <ul className="divide-y divide-border">
              {e.reminders.map((r, i) => (
                <li key={i} className="flex items-center gap-3 px-4 py-3 text-sm">
                  <div className="flex gap-1">
                    {r.channels.map((c) => <Badge key={c} tone="neutral">{c}</Badge>)}
                  </div>
                  <span className="text-ink-muted">·</span>
                  <span>{formatOffset(r.offset)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader><CardTitle>Delivery status</CardTitle></CardHeader>
        <CardBody className="p-0">
          {Object.keys(e.delivery_status).length === 0 ? (
            <p className="px-4 py-3 text-sm text-ink-muted">No delivery activity yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {(['voice', 'sms', 'email', 'identity_verify'] as const).map((ch) => {
                const status = e.delivery_status[ch];
                if (!status) return null;
                return (
                  <li key={ch} className="flex items-center justify-between px-4 py-3 text-sm">
                    <span className="font-medium">{ch.replace('_', ' ')}</span>
                    <Badge tone={DELIVERY_TONE[status]}>{status.replace('_', ' ')}</Badge>
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>

      {timelineQ.data && <DeliveryHistory tl={timelineQ.data} />}

      <p className="text-xs text-ink-muted text-right">
        Created {new Date(e.created_at).toLocaleString()} · updated {new Date(e.updated_at).toLocaleString()}
      </p>
    </div>
  );
}

interface EventTimeline {
  state_changes: Array<{
    id: string;
    channel: string;
    old_status: string | null;
    new_status: string;
    captured_at: string;
    command_id: string | null;
    metadata: Record<string, unknown> | null;
  }>;
  commands: Array<{
    id: string;
    type: string;
    status: string;
    created_at: string;
    ack_at: string | null;
    completed_at: string | null;
    last_error: Record<string, unknown> | null;
  }>;
  webhook_attempts: Array<{
    id: string;
    subscription_id: string;
    status: string;
    attempt_count: number;
    next_attempt_at: string | null;
    last_error: string | null;
    created_at: string;
  }>;
}

function DeliveryHistory({ tl }: { tl: EventTimeline }): JSX.Element {
  return (
    <Card>
      <CardHeader><CardTitle>Delivery history</CardTitle></CardHeader>
      <CardBody className="space-y-5">
        <Section title="State changes" empty="No state transitions yet.">
          {tl.state_changes.length > 0 && (
            <ul className="text-sm divide-y divide-border">
              {tl.state_changes.map((s) => (
                <li key={s.id} className="py-2 grid grid-cols-[auto_auto_1fr] gap-2 items-center">
                  <span className="font-mono text-xs text-ink-muted w-40">
                    {new Date(s.captured_at).toLocaleString()}
                  </span>
                  <Badge tone="neutral">{s.channel}</Badge>
                  <span>
                    {s.old_status ? <code className="text-ink-muted">{s.old_status}</code> : <em className="text-ink-muted">∅</em>}
                    <span className="text-ink-muted px-1">→</span>
                    <code>{s.new_status}</code>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Connector commands" empty="No connector commands dispatched.">
          {tl.commands.length > 0 && (
            <ul className="text-sm divide-y divide-border">
              {tl.commands.map((c) => (
                <li key={c.id} className="py-2 grid grid-cols-[auto_auto_1fr_auto] gap-2 items-center">
                  <span className="font-mono text-xs text-ink-muted w-40">
                    {new Date(c.created_at).toLocaleString()}
                  </span>
                  <Badge tone="neutral">{c.type}</Badge>
                  <span className="text-xs text-ink-muted truncate" title={c.id}>{c.id}</span>
                  <Badge
                    tone={
                      c.status === 'completed' ? 'success'
                      : c.status === 'failed' ? 'danger'
                      : c.status === 'in_flight' ? 'info'
                      : 'warning'
                    }
                  >
                    {c.status}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Webhook attempts" empty="No webhook attempts.">
          {tl.webhook_attempts.length > 0 && (
            <ul className="text-sm divide-y divide-border">
              {tl.webhook_attempts.map((w) => (
                <li key={w.id} className="py-2 grid grid-cols-[auto_1fr_auto_auto] gap-2 items-center">
                  <span className="font-mono text-xs text-ink-muted w-40">
                    {new Date(w.created_at).toLocaleString()}
                  </span>
                  <span className="text-xs text-ink-muted truncate" title={w.subscription_id}>
                    {w.subscription_id}
                  </span>
                  <span className="text-xs text-ink-muted">×{w.attempt_count}</span>
                  <Badge
                    tone={
                      w.status === 'delivered' ? 'success'
                      : w.status === 'failed' ? 'danger'
                      : w.status === 'in_flight' ? 'info'
                      : 'warning'
                    }
                  >
                    {w.status}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </CardBody>
    </Card>
  );
}

function Section({
  title,
  empty,
  children,
}: {
  title: string;
  empty: string;
  children: React.ReactNode;
}): JSX.Element {
  const hasChildren = !!children && (Array.isArray(children) ? children.some(Boolean) : true);
  return (
    <div>
      <h3 className="text-sm font-medium text-ink mb-2">{title}</h3>
      {hasChildren ? children : <p className="text-sm text-ink-muted">{empty}</p>}
    </div>
  );
}

function DefList({ items }: { items: [string, React.ReactNode][] }): JSX.Element {
  return (
    <dl className="grid grid-cols-[140px_1fr] gap-y-2 text-sm">
      {items.map(([k, v], i) => (
        <span key={i} className="contents">
          <dt className="text-ink-muted">{k}</dt>
          <dd>{v}</dd>
        </span>
      ))}
    </dl>
  );
}

function RecurrenceSummary({ rule }: { rule: RecurrenceRule }): JSX.Element {
  const cap = rule.count ? `, ${rule.count} occurrences`
    : rule.until ? `, until ${new Date(rule.until).toLocaleDateString()}`
    : '';
  if (rule.frequency === 'weekdays') {
    return <span>Weekdays{rule.weekdays?.length ? ` (${rule.weekdays.join(', ')})` : ''}{cap}</span>;
  }
  const each = rule.interval > 1 ? `Every ${rule.interval} ${rule.frequency}` : `Every ${rule.frequency.replace(/ly$/, '')}`;
  return <span>{each}{cap}</span>;
}

/**
 * Render an ISO 8601 duration (PT24H / P1W / P1M) in human terms; if it's an
 * absolute datetime, show the formatted date. Mirrors the duration parser in
 * the App's scheduler — keep these in sync.
 */
function formatOffset(s: string): string {
  if (s.startsWith('P')) {
    const map: Record<string, string> = {
      'PT1H': '1 hour before',
      'PT24H': '24 hours before',
      'PT48H': '48 hours before',
      'PT72H': '72 hours before',
      'P1D': '1 day before',
      'P1W': '1 week before',
      'P1M': '1 month before',
    };
    return map[s] ?? `${s} before`;
  }
  return `at ${new Date(s).toLocaleString()}`;
}
